<template>
  <v-layout wrap>
    <Loading :isLoading="isLoading">
      <v-flex xs12>
        <span
          v-if="isLinked && !embedded"
          class="caption opacity-54 mb-2 d-block"
          >{{ formatName(field.name) }}</span
        >
        <v-card
          flat
          v-if="isLinked && !embedded"
          class="px-4 py-3 mb-4"
          color="transparent"
          style="background-color:rgba(0,0,0,0.06) !important; border-top-left-radius: 4px; border-top-right-radius: 4px; !important; border-bottom:1px solid rgba(0,0,0,0.22) !important"
        >
          <v-card-text class="pa-0">
            <v-layout column pr-4 mr-2>
              <table class="v-datatable v-table mt-0">
                <tr v-for="(field, index) in relatedHeaders" :key="index">
                  <td class="pa-0 relation-field-label">
                    <span class="caption opacity-54"
                      >{{ formatName(field) }}:</span
                    >
                  </td>
                  <td>
                    <label v-if="getRelatedFieldDisplayValue(field)">
                      <router-link :to="relationRoute">{{
                        getRelatedFieldDisplayValue(field)
                      }}</router-link>
                    </label>
                  </td>
                </tr>
              </table>
            </v-layout>
          </v-card-text>
          <v-card-actions
            v-if="!readonly"
            class="px-0 pt-0 pb-0 relation-actions"
          >
            <v-btn
              class="ml-0 btn--plain"
              :ripple="false"
              flat
              color="error"
              @click="unlink()"
            >
              <v-icon class="mr-2" left dark>cancel</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-flex>
      <v-flex v-if="!readonly && !isLinked && !embedded" xs12 class="mb-4">
        <v-autocomplete
          box
          dense
          clearable
          no-filter
          v-model="quickSearchSelection"
          :search-input.sync="quickSearchData"
          :items="quickSearchCases"
          :label="formatName(field.name)"
          @keyup="quickSearch(quickSearchData)"
          @focus="quickSearch()"
          placeholder="Start typing to search"
          v-on:click:clear="unlink()"
          item-text="name"
          item-value="value"
        ></v-autocomplete>
        <v-layout
          wrap
          row
          fluid
          ma-0
          style="margin-top: -18px !important"
          v-if="!embedded"
        >
        <RelationFieldSearchDialog :field=field :caseType=caseType :preferences=preferences v-on:link="link" v-if="caseType"></RelationFieldSearchDialog> &nbsp;
        <RelationFieldAddCaseDialog :field=field :caseType=caseType v-on:set-related-case="setSummary" v-if="addAllowed"></RelationFieldAddCaseDialog>
        </v-layout>
      </v-flex>
      <v-flex v-if="embedded" xs12 class="mb-4">
        <v-autocomplete
          box
          dense
          no-filter
          v-model="quickSearchSelection"
          :search-input.sync="quickSearchData"
          :return-object="true"
          :items="quickSearchCases"
          :label="formatName(field.name)"
          @keyup="quickSearch(quickSearchData)"
          @focus="quickSearch()"
          :disabled="readonly"
          placeholder="Start typing to search"
          item-text="name"
          item-value="value"
          append-icon="keyboard_arrow_down"
          class="with-vert-menu"
        >
          <template v-slot:append-outer>
            <v-menu>
              <v-btn
                flat
                slot="activator"
                icon
                class="ma-0 pa-0"
                dark
                color="secondary"
              >
                <v-icon>more_vert</v-icon>
              </v-btn>
              <v-list dense>
                <v-list-tile
                  v-if="!readonly"
                  @click="unlink"
                  class="embedded-relation-clear-button"
                >
                  <v-icon class="mr-2" color="secondary">cancel</v-icon>Clear
                </v-list-tile>
                <v-list-tile
                  @click="navigateToRelatedCase"
                  class="embedded-relation-view-button"
                >
                  <v-icon class="mr-2" color="secondary">attach_file</v-icon>Go
                  to case
                </v-list-tile>
              </v-list>
            </v-menu>
          </template>
        </v-autocomplete>
      </v-flex>
    </Loading>
  </v-layout>
</template>

<script>
import { mapState } from "vuex";
import Loading from "@/components/shared/Loading.vue";
import RelationFieldSearchDialog from "@/components/cases/case-elements/RelationFieldSearchDialog.vue";
import RelationFieldAddCaseDialog from  "@/components/cases/case-elements/RelationFieldAddCaseDialog.vue";
import Helpers from "@/helpers.js";

export default {
  name: "RelationField",
  props: {
    field: {
      type: Object,
      required: true
    },
    data: {
      type: Object,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    addAllowed: {
      type: Boolean,
      default: false,
    },
    row: {
      type: Number,
    },
  },
  components: { Loading, RelationFieldSearchDialog, RelationFieldAddCaseDialog },
  data: () => {
    return {
      tableHeaders: [],
      tableItems: [],
      dialogNew: false,
      dialogSearch: false,
      searchObj: {},
      quickSearchData: "",
      quickSearchCases: [],
      quickSearchSelection: {},
      summary: {},
      // Skip link() while hydrating autocomplete from existing relation data.
      // Vue 2 watchers flush async — clear this flag in $nextTick after setting selection.
      suppressQuickSearchLink: false,
    };
  },
  async mounted() {
    if (this.data) {
      this.initQuickSearchSelection(this.data);
      await this.setSummary(this.data.id);
    }
  },
  computed: {
    ...mapState({
      preferences: (state) => state.currentUser.me.preferences,
      caseTypes: (state) => state.caseTypes.all,
      isLoading: (state) => state.cases.loading,
      currentCase: (state) => state.cases.currentlySelected.case,
    }),
    isLinked() {
      return this.data && this.data.id;
    },
    caseType() {
      return (this.caseTypes || []).find((currentType) => currentType.code === this.field.relation)
    },
    fields() {
      if (this.caseType && this.caseType.displayFields) {
        return this.caseType.displayFields;
      } else {
        return [];
      }
    },
    keys() {
      return this.fields.map((field) => field.fieldName);
    },
    relations: {
      get() {
        const relations = [];
        const keys = this.keys;
        const data = this.data;
        if (keys.length > 0) {
          Object.keys(data).forEach(function (key) {
            if (keys.includes(key)) {
              relations.push({
                label: Helpers.capitalizeRemoveUnderscores(key),
                data: data[key],
              });
            }
          });
        } else {
          Object.keys(data).forEach(function (key) {
            if (key !== "_id" && data[key] !== null) {
              relations.push({
                label: Helpers.capitalizeRemoveUnderscores(key),
                data: data[key],
              });
            }
          });
        }
        return relations;
      },
      set(newRelations) {
        this.$emit("update-item", {
          field: this.field.name,
          data: newRelations,
          row: this.row,
        });
        this.$emit("blurred", {
          field: this.field.name,
          data: newRelations,
          row: this.row,
        });
      },
    },
    relationRoute() {
      if (this.isLinked) {
        return { name: "Case", params: { case_id: this.data.id } };
      }
      return undefined;
    },
    readonly() {
      let readonly = false;
      if (this.field && this.field.readonly) {
        readonly = this.field.readonly;
      }
      return readonly;
    },
    relatedHeaders() {
      const caseType = Helpers.getCaseTypeByCode(
        this.$store.state,
        this.field.relation
      );

      
      if (caseType && caseType.displayFields) {
        return caseType.displayFields.map((field) => field.fieldName);
      } else {
        console.warn("Unable to get display fields for case type", this.field.relation);
        return [];
      }
    },
  },
  watch: {
    quickSearchSelection(newVal) {
      if (this.suppressQuickSearchLink) {
        return;
      }
      if (!newVal || !this.selectionHasRelationId(newVal)) {
        return;
      }
      this.link(newVal);
    },
    async data(newData, oldData) {
      if (newData) {
        if (!oldData || (oldData && newData.id !== oldData.id)) {
          this.initQuickSearchSelection(this.data);
          return await this.setSummary(newData.id);
        }
      }
    },
  },
  methods: {
    getRelatedFieldDisplayValue(fieldName) {
      if (this.summary && this.summary[fieldName]) {
        return this.formatSummaryFieldValue(this.summary, fieldName);
      }
      if (this.data && this.data.properties && this.data.properties[fieldName]) {
        return this.data.properties[fieldName];
      }
      if (this.data && this.data[fieldName]) {
        return this.data[fieldName];
      }
      return null;
    },
    addRelationCase(field) {
      console.log("add relation case", field);
    },
    determineSearchTermSuffix(field, currentCase) {
      if (field.lookup_defaults && currentCase && currentCase.properties) {
        if (field.lookup_defaults.type.toLowerCase() === "reference") {
          const value = currentCase.properties[field.lookup_defaults.value];
          return ` AND ${field.lookup_defaults.lookup_field_name}:${value}`;
        } else {
          return ` AND ${field.lookup_defaults.lookup_field_name}:${field.lookup_defaults.value}`;
        }
      }
    },
    formatSummaryFieldValue(summary, field) {
      return summary[field];
    },
    getRelateableItems() {
      const total = this.caseType.buckets[0].statistics.total;
      if (this.caseType !== undefined) {
        this.$store.dispatch("getBucketCases", {
          bucketId: this.caseType.buckets[0].id,
          page: 1,
          pageSize: total,
        });
      }
    },
    async quickSearch() {
      let searchTerm = "*";
      if (this.quickSearchData !== null) {
        searchTerm = `${this.quickSearchData}*`;
      }
      // find the value 1 amongst the company_ref fields  in the schema on the current case
      const searchTermSuffix = this.determineSearchTermSuffix(
        this.field,
        this.currentCase
      );

      if (searchTermSuffix) {
        searchTerm += searchTermSuffix;
      }
      const searchData = {
        caseType: this.field.relation,
        page: 0,
        pageSize: Helpers.RELATION_LOOKUP_PAGE_SIZE,
        query: searchTerm,
        fieldsToReturn: ["all_fields"],
      };
      const result = await this.$store.dispatch("getCaseSearch", searchData);
      const cases = [];
      if (result) {
        this.prepareSearchResults(result);
        result.forEach(function (item) {
          cases.push({ name: item.title, value: item, id: item._id });
        });
      }
      this.quickSearchCases = cases;
    },
    getSummaryFields() {
      const fields = [];
      if (this.summary) {
        for (const field in this.summary) {
          fields.push(field);
        }
      }
      if (fields.length === 0) {
        return ["title"];
      }
      return fields;
    },
    prepareSearchResults(results) {
      if (this.embedded && results && Array.isArray(results)) {
        const summaryFields = this.getSummaryFields();

        results.forEach((r) => {
          if (r && r.properties) {
            const newSummaryProps = {};

            summaryFields.forEach((sfield) => {
              newSummaryProps[sfield] = r.properties[sfield];
            });
            r.properties = newSummaryProps;
          }
          delete r.__typename;
        });
      }
    },
    
    async setSummary(caseId) {
      try {
        const caseSummary = await this.$store.dispatch("getCaseSummaryById", { caseId });
        if (caseSummary) {
          const summary = { id: caseId, properties: {} };
          for (let index = 1; index <= 5; index++) {
            const defn1 = caseSummary[`summaryVal${index}Defn`];
            const defn = typeof defn1 === "string" ? JSON.parse(defn1) : defn1;
            if (defn) {
              let valstr = caseSummary[`summaryVal${index}`];
              if (valstr) {
                if (defn.type === "date") {
                  if (valstr.includes('"')) {
                    // If Date arrives in quotes..
                    valstr = valstr.replace(/"/g, "");
                  }
                  const format = this.preferences.dateTimeFormat
                    ? this.preferences.dateTimeFormat
                    : "HH:MM, D MMM";
                  valstr = Helpers.formatDate(valstr, format);
                } else if (defn.type === "relation") {
                  try {
                    const relationValue = JSON.parse(valstr);
                    if (relationValue && relationValue.properties) {
                      valstr = Object.values(relationValue.properties).join(", ");
                    }
                  } catch (error) {
                    console.error("Unable to parse summary field value.");
                    console.error(error);
                  }
                } else if (
                  defn.type === "number" &&
                  defn.display === "currency"
                ) {
                  const currencyNumber = parseFloat(valstr);
                  valstr = Helpers.formatCurrency(currencyNumber, defn.symbol);
                }
                summary.properties[defn.name] = valstr;
              }
            }
          }
          // Display only: assigning to `relations` would emit these formatted values and save them onto the case.
          this.summary = summary.properties;
          if (!this.embedded) {
            this.quickSearchData = Object.values(summary.properties).join(" ");
          }
        }
      } catch (error) {
        // Keep existing relation value if summary lookup fails.
        console.error("Failed to load relation summary", error);
      }
    },
    selectionHasRelationId(relationship) {
      if (!relationship || typeof relationship !== "object") {
        return false;
      }
      if (this.embedded) {
        return !!(
          relationship.id ||
          (relationship.value && relationship.value._id)
        );
      }
      return !!(relationship._id || relationship.id);
    },
    // Autocomplete items are { id, name, value: { properties } }; dialogs may pass a case-shaped object.
    resolveLinkPayload(relationship) {
      if (!relationship || typeof relationship !== "object") {
        return { id: null, properties: {} };
      }
      let id = null;
      let properties = {};
      if (this.embedded) {
        id = relationship.id;
        if (relationship.value) {
          if (!id && relationship.value._id) {
            id = relationship.value._id;
          }
          if (relationship.value.properties) {
            properties = Object.assign({}, relationship.value.properties);
          }
        }
        if (relationship.properties) {
          properties = Object.assign({}, relationship.properties);
        }
      } else {
        id = relationship._id || relationship.id;
        if (relationship.properties) {
          properties = Object.assign({}, relationship.properties);
        }
      }
      return { id: id, properties: properties };
    },
    async link(relationship) {
      if (relationship && !this.readonly) {
        const payload = this.resolveLinkPayload(relationship);
        const relationShipId = payload.id;
        // Emit the relation object immediately so save stores it (API expects relation with id; ref is synced server-side)
        if (relationShipId) {
          const relationData = { id: relationShipId, properties: payload.properties };
          this.$emit("update-item", {
            field: this.field.name,
            data: relationData,
            row: this.row,
          });
          this.$emit("blurred", {
            field: this.field.name,
            data: relationData,
            row: this.row,
          });
        }
        if (
          this.field.uif &&
          payload.properties &&
          payload.properties[this.field.uif] !== undefined
        ) {
          this.$emit("update-item", {
            field: this.field.uif,
            data: payload.properties[this.field.uif],
            row: this.row,
          }); // updates the X_ref field after relation has been picked
        }
        if (relationShipId) {
          await this.setSummary(relationShipId);
        }
      }
    },
    unlink() {
      if (!this.readonly) {
        this.quickSearchData = "";
        this.quickSearchSelection = {};
        this.searchObj = {};
        this.$emit("update-item", {
          field: this.field.name,
          data: null,
          row: this.row,
        }); // clear field
        this.$emit("blurred", {
          field: this.field.name,
          data: null,
          row: this.row,
        });
        if (this.field.uif) {
          this.$emit("update-item", {
            field: this.field.uif,
            data: null,
            row: this.row,
          }); // clear field
        }
      }
    },
    formatName(name) {
      return Helpers.capitalizeRemoveUnderscores(name);
    },
    prepareFieldset(name) {
      return Helpers.capitalizeRemoveUnderscores(name);
    },
    navigateToRelatedCase() {
      if (this.relationRoute) {
        this.$router.push(this.relationRoute);
      }
    },
    initQuickSearchSelection(data) {
      if (
        this.embedded &&
        data.id &&
        data.properties &&
        data.properties.title
      ) {
        // Prevent the quickSearchSelection watcher from re-linking hydrate data
        // (wrong shape → empty properties wipe). Watchers flush on next tick.
        this.suppressQuickSearchLink = true;
        this.quickSearchSelection = {
          id: data.id,
          name: data.properties.title,
          value: {
            _id: data.id,
            title: data.properties.title,
            properties: data.properties,
          },
        };
        this.quickSearchCases.push(this.quickSearchSelection);
        this.$nextTick(() => {
          this.suppressQuickSearchLink = false;
        });
      }
    },
  },
};
</script>

<style>
.red-clear-icon .v-input__icon--clear .theme--light.v-icon {
  color: #db444a !important;
}

.relation-data table {
  background: none !important;
}

.relation-data tr {
  background: none !important;
  border: none !important;
}

.relation-data td {
  padding: 5px 0 !important;
  background: none !important;
  height: auto !important;
  font-size: 14px !important;
}

.relation-field-label {
  max-width: 190px !important;
  width: 190px !important;
}

.relation-actions {
  position: absolute;
  top: 50%;
  margin-top: -10px;
  right: 10px;
}

.with-vert-menu.v-autocomplete.v-text-field--enclosed:not(.v-text-field--solo):not(.v-text-field--single-line)
  .v-select__slot
  > input {
  padding-right: 50px;
  position: relative;
}
.with-vert-menu.v-text-field.v-text-field--enclosed .v-input__append-outer {
  position: absolute;
  right: 38px;
  top: -4px;
  pointer-events: auto;
}
</style>
